import React from 'react';
import { Link } from 'react-router-dom';

/**
 * AI IMAGE PROMPTS:
 * 
 * feature-ai-analysis.jpg:
 * "Modern dashboard showing AI performance analytics, colorful charts and graphs, 
 * holographic data visualization, student performance metrics, purple and blue color scheme, 
 * futuristic UI design, 3D elements, high tech, clean and professional, 8K quality"
 * 
 * feature-voice-tutor.jpg:
 * "Indian student speaking into microphone with AI voice waves visualization, 
 * holographic AI assistant interface, sound wave patterns in blue and purple, 
 * modern technology, interactive learning, warm lighting, photorealistic, 8K"
 * 
 * feature-mock-tests.jpg:
 * "Digital quiz interface on tablet with floating question cards, 
 * AI analyzing answers in real-time, holographic checkmarks and scores, 
 * modern educational technology, blue and green accents, clean design, 8K"
 * 
 * feature-progress-tracking.jpg:
 * "3D growth chart with upward trending arrows, achievement badges floating, 
 * progress bars and milestones, AI-powered analytics, gradient colors blue to purple, 
 * motivational and inspiring, modern tech aesthetic, 8K quality"
 * 
 * feature-study-plan.jpg:
 * "Personalized AI-generated study calendar with subject icons, 
 * holographic schedule interface, brain with neural network connections, 
 * organized learning path, purple and gold accents, futuristic, 8K"
 * 
 * feature-leaderboard.jpg:
 * "Competitive ranking board with student avatars, trophy and medals, 
 * holographic leaderboard display, achievement badges, gold silver bronze colors, 
 * motivational gaming aesthetic, modern design, 8K quality"
 */

interface Feature {
  title: string;
  description: string;
  icon: string;
  color: string;
  imagePlaceholder: string;
  benefits: string[];
}

const AIFeaturesSection: React.FC = () => {
  const features: Feature[] = [
    {
      title: "AI Performance Analysis",
      description: "Get comprehensive insights into your preparation with 30+ detailed metrics analyzed by artificial intelligence.",
      icon: "fas fa-brain",
      color: "#667eea",
      imagePlaceholder: "feature-ai-analysis.jpg",
      benefits: [
        "Subject-wise strength analysis",
        "Topic-level gap identification",
        "Personalized recommendations",
        "Time management insights"
      ]
    },
    {
      title: "Interactive AI Mentor (WiziTalk)",
      description: "Ask questions naturally using voice and get instant, detailed explanations from our AI mentor.",
      icon: "fas fa-microphone-alt",
      color: "#764ba2",
      imagePlaceholder: "feature-voice-tutor.jpg",
      benefits: [
        "24/7 instant doubt solving",
        "Natural voice interaction",
        "Step-by-step explanations",
        "Context-aware responses"
      ]
    },
    {
      title: "Smart Mock Tests",
      description: "Experience exam-like conditions with AI-curated mock tests that adapt to your skill level and learning pace.",
      icon: "fas fa-clipboard-check",
      color: "#2d9cdb",
      imagePlaceholder: "feature-mock-tests.jpg",
      benefits: [
        "Adaptive difficulty levels",
        "Real exam simulation",
        "Instant result analysis",
        "Performance comparison"
      ]
    },
    {
      title: "Progress Tracking",
      description: "Monitor your improvement journey with detailed analytics, visual charts, and milestone achievements.",
      icon: "fas fa-chart-line",
      color: "#27ae60",
      imagePlaceholder: "feature-progress-tracking.jpg",
      benefits: [
        "Historical performance trends",
        "Goal achievement tracking",
        "Visual progress charts",
        "Improvement predictions"
      ]
    },
    {
      title: "Personalized Study Plan",
      description: "AI creates customized study schedules based on your strengths, weaknesses, and target exam dates.",
      icon: "fas fa-calendar-alt",
      color: "#f39c12",
      imagePlaceholder: "feature-study-plan.jpg",
      benefits: [
        "AI-optimized schedules",
        "Adaptive task planning",
        "Weakness-focused learning",
        "Time-efficient preparation"
      ]
    },
    {
      title: "Smart Leaderboard (WiziBoard)",
      description: "Stay motivated with competitive rankings, compare progress with peers, and track your position.",
      icon: "fas fa-trophy",
      color: "#e74c3c",
      imagePlaceholder: "feature-leaderboard.jpg",
      benefits: [
        "Real-time rankings",
        "Peer comparison",
        "Achievement badges",
        "Competitive motivation"
      ]
    }
  ];

  return (
    <div className="ai-features-section py-5" style={{ background: '#f8f9fa' }}>
      <div className="container py-5">
        
        {/* Section Header */}
        <div className="text-center mb-5 pb-4">
          <div className="d-inline-flex align-items-center px-4 py-2 mb-3 rounded-pill"
            style={{
              background: 'linear-gradient(135deg, #667eea15, #764ba215)',
              border: '1px solid #667eea30'
            }}>
            <i className="fas fa-robot me-2 text-primary"></i>
            <span className="fw-semibold text-primary">AI-Powered Features</span>
          </div>
          <h2 className="display-4 fw-bold mb-3">
            Your Intelligent <span style={{ 
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Study Companion</span>
          </h2>
          <p className="lead text-muted mb-0" style={{ maxWidth: '700px', margin: '0 auto' }}>
            Experience the future of exam preparation with cutting-edge AI technology 
            that understands your learning needs and accelerates your success.
          </p>
        </div>

        {/* Features Grid */}
        <div className="row g-4">
          {features.map((feature, index) => (
            <div key={index} className="col-lg-4 col-md-6">
              <div className="card h-100 border-0 shadow-sm feature-card-glassmorphism"
                style={{
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}>
                
                {/* Feature Image */}
                <div className="position-relative" style={{ height: '200px', overflow: 'hidden' }}>
                  <img 
                    src={`/img/home-v2/${feature.imagePlaceholder}`}
                    alt={feature.title}
                    className="w-100 h-100"
                    style={{ objectFit: 'cover', borderBottom: `3px solid ${feature.color}` }}
                  />
                  
                  {/* Glassmorphism Overlay Badge */}
                  <div className="position-absolute top-0 end-0 m-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '50px',
                        height: '50px',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: `2px solid ${feature.color}`,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}>
                      <i className={feature.icon} style={{ color: feature.color }}></i>
                    </div>
                  </div>
                </div>

                <div className="card-body p-4">
                  <h5 className="card-title fw-bold mb-3" style={{ color: feature.color }}>
                    {feature.title}
                  </h5>
                  <p className="card-text text-muted mb-4">
                    {feature.description}
                  </p>

                  {/* Benefits List */}
                  <ul className="list-unstyled mb-4">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="d-flex align-items-start mb-2">
                        <i className="fas fa-check-circle me-2 mt-1" 
                          style={{ color: feature.color, fontSize: '0.9rem' }}></i>
                        <small className="text-muted">{benefit}</small>
                      </li>
                    ))}
                  </ul>

                  <Link to="/features" 
                    className="btn btn-sm w-100 rounded-pill"
                    style={{
                      background: `${feature.color}15`,
                      border: `2px solid ${feature.color}`,
                      color: feature.color,
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = feature.color;
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `${feature.color}15`;
                      e.currentTarget.style.color = feature.color;
                    }}>
                    Learn More <i className="fas fa-arrow-right ms-1"></i>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-5 pt-4">
          <div className="rounded-4 p-5"
            style={{
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              position: 'relative',
              overflow: 'hidden'
            }}>
            <div className="position-relative" style={{ zIndex: 1 }}>
              <h3 className="text-white fw-bold mb-3">
                Ready to Experience AI-Powered Learning?
              </h3>
              <p className="text-white mb-4" style={{ opacity: 0.9, fontSize: '1.1rem' }}>
                Join thousands of students already achieving their dreams with WiziAI
              </p>
              <Link to="/signup" 
                className="btn btn-light btn-lg px-5 py-3 rounded-pill shadow-lg">
                <i className="fas fa-rocket me-2"></i>
                Start Your Free Trial
              </Link>
            </div>
            
            {/* Background Pattern */}
            <div className="position-absolute w-100 h-100 top-0 start-0" style={{ opacity: 0.1 }}>
              <div className="position-absolute rounded-circle" 
                style={{ width: '300px', height: '300px', background: 'white', top: '-20%', right: '-10%' }}></div>
              <div className="position-absolute rounded-circle" 
                style={{ width: '200px', height: '200px', background: 'white', bottom: '-10%', left: '-5%' }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIFeaturesSection;
